import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import BodyType from '../../../../types/body-type/BodyType';
import BodyTypeWriteDTO from '../../../../types/body-type/BodyTypeWriteDTO';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { BodyTypeService } from '../../services/body-type.service';
import { ExportService } from '../../services/export.service';
import {
    CreateBodyTypeDialogComponent
} from '../shared/body-types/create-body-type-dialog/create-body-type-dialog.component';
import {
    UpdateBodyTypeDialogComponent
} from '../shared/body-types/update-body-type-dialog/update-body-type-dialog.component';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';

@Component({
    selector: 'app-body-types',
    templateUrl: './body-types.component.html',
    styleUrls: [ './body-types.component.scss' ],
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateBodyTypeDialogComponent,
        UpdateBodyTypeDialogComponent,
        InputText,
        RouterLink,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip
    ]
})
export class BodyTypesComponent implements OnInit {
    public search = '';
    public bodyTypes: BodyType[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateBodyTypeDialog = false;
    public displayUpdateBodyTypeDialog = false;
    public selectedBodyType: BodyTypeWriteDTO = new BodyTypeWriteDTO({});

    private searchSubject = new Subject<string>();

    constructor(private bodyTypeService: BodyTypeService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private exportService: ExportService) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.loadBodyTypes();

        // Debounce the search input to prevent too many requests
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0; // Reset to the first page on new search
            this.loadBodyTypes();
        });
    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadBodyTypes();
    }

    /**
     * Handles the search input change event.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Edits the body type.
     * @param bodyType The body type to edit.
     */
    public editBodyType(bodyType: BodyType): void {
        this.selectedBodyType = new BodyTypeWriteDTO(bodyType);
        this.displayUpdateBodyTypeDialog = true;
    }

    /**
     * Deletes the body type.
     * @param bodyType The body type to delete.
     */
    public deleteBodyType(bodyType: BodyType): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the body type '${ bodyType.name }'?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.bodyTypeService.deleteBodyType(bodyType.id).then(() => {
                    this.loadBodyTypes();
                    this.messageService.add(
                        { severity: 'success', summary: 'Success', detail: 'Body Type deleted', life: 3000 }
                    );
                }).catch((error: any) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the create body type dialog.
     */
    public openCreateBodyTypeDialog(): void {
        this.displayCreateBodyTypeDialog = true;
    }

    /**
     * Reloads the body types.
     */
    public reloadBodyTypes(): void {
        this.loadBodyTypes();
    }

    /**
     * Exports the body types.
     */
    public exportBodyTypes(): void {
        this.exportService.exportBodyTypes().then(() => {
            this.messageService.add(
                { severity: 'success', summary: 'Success', detail: 'Body Types exported', life: 3000 }
            );
        }).catch((error: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
        });
    }

    /**
     * Loads the body types.
     */
    private loadBodyTypes(): void {
        this.loading = true;

        const size = this.rows;
        const search = this.search || '';

        this.bodyTypeService
            .getBodyTypes(search, this.currentPage, size)
            .then((result: Page<BodyType>) => {
                this.bodyTypes = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }
}
